import { Injectable } from '@angular/core';
import { Token } from '@cargos/sprintpay-models';
import { TokenService } from '@cargos/sprintpay-services';

@Injectable({
    providedIn: 'root',
})
export class JwtHelperService {
    constructor(private tokenService: TokenService) {}

    private tokenGetter(): string {
        return this.tokenService.instant_token;
    }

    urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0: {
                break;
            }
            case 2: {
                output += '==';
                break;
            }
            case 3: {
                output += '=';
                break;
            }
            default: {
                throw new Error('Illegal base64url string!');
            }
        }
        return this.b64DecodeUnicode(output);
    }

    b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';

        str = String(str).replace(/=+$/, '');

        let bs: number = 0;

        if (str.length % 4 === 1) {
            throw new Error(`'atob' failed: The string to be decoded is not correctly encoded.`);
        }

        for (
            let bc = 0, buffer: string | number, idx = 0;
            (buffer = str.charAt(idx++));
            ~buffer && ((bs = bc % 4 ? bs * 64 + buffer : buffer), bc++ % 4)
                ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0
        ) {
            buffer = chars.indexOf(buffer);
        }
        return output;
    }

    b64DecodeUnicode(str: string): string {
        return decodeURIComponent(
            Array.prototype.map
                .call(this.b64decode(str), (c: string) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
    }

    decodeToken<T = unknown>(token: string = this.tokenGetter()): null | T {
        return this._decodeToken(token);
    }

    private _decodeToken<T = unknown>(token: string): null | T {
        if (!token || token === '') {
            return null;
        }

        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error(
                `The inspected token doesn't appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.`
            );
        }

        const decoded = this.urlBase64Decode(parts[1]);
        if (!decoded) {
            throw new Error('Cannot decode the token.');
        }

        return JSON.parse(decoded);
    }

    getTokenExpirationDate(token: string = this.tokenGetter()): Date | null {
        return this._getTokenExpirationDate(token);
    }

    private _getTokenExpirationDate(token: string): Date | null {
        let decoded: Token | null;
        decoded = this.decodeToken(token);

        if (!decoded || !decoded.hasOwnProperty('exp')) {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);

        return date;
    }

    isTokenExpired(token: undefined | null | string = this.tokenGetter(), offsetSeconds?: number): boolean {
        return this._isTokenExpired(token, offsetSeconds);
    }

    private _isTokenExpired(token: string | null, offsetSeconds?: number): boolean {
        if (!token || token === '') {
            return true;
        }
        const date = this.getTokenExpirationDate(token);
        offsetSeconds = offsetSeconds || 0;

        if (date === null) {
            return false;
        }

        const newDate: number = new Date().valueOf();

        return !(date.valueOf() > newDate + offsetSeconds * 1000);
    }

    isTokenValid(token: string): boolean {
        if (!token || token === '') {
            return false;
        }

        const parts = token.split('.');

        if (parts.length !== 3) {
            return false;
        }

        const decoded = this.urlBase64Decode(parts[1]);

        if (!decoded) {
            return false;
        }

        return true;
    }
}
